/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	ChangeEvent,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';

import { InputText } from 'primereact/inputtext';
import { Column, ColumnEditorOptions } from 'primereact/column';
import {
	DataTable,
	DataTablePFSEvent,
	DataTableSortOrderType
} from 'primereact/datatable';
import { PaginatorPageState } from 'primereact/paginator';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { InputSwitch, InputSwitchChangeParams } from 'primereact/inputswitch';

import Filters from './filters/Filters';
import Pagination from '../common/pagination/Pagination';
import { DropdownOption } from '../../models/common/DropdownOption';
import { BrandsSearchResponseModel } from '../../models/brands/BrandsSearchResponseModel';
import { BrandsListingResponseModel } from '../../models/brands/BrandsListingResponseModel';
import { HttpService, IHttpService } from '../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../services/notifierService';
import { nameof } from '../../shared/utility';
import { useCookies } from 'react-cookie';

const Brands = (): JSX.Element => {
	const [loading, setLoading] = useState<boolean>(false);
	const [first, setFirst] = useState<number>(0);
	const [rows, setRows] = useState<number>(10);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [sortField, setSortField] = useState<string>('');
	const [sortOrder, setSortOrder] = useState<DataTableSortOrderType>();
	const [brandCodeFilter, setBrandCodeFilter] = useState<string>('');
	const [brandDescriptionFilter, setBrandDescriptionFilter] =
		useState<string>('');
	const [brandTypeFilter, setBrandTypeFilter] = useState<string | number>('');
	const [shouldReload, setShouldReload] = useState<boolean>(false);
	const [brandsSearch, setBrandsSearch] =
		useState<BrandsSearchResponseModel>();
	const [brandsListing, setBrandsListing] = useState<
		BrandsListingResponseModel[]
	>([]);
	const [brandTypesOptions, setBrandTypesOptions] = useState<
		DropdownOption[]
	>([]);

	const [cookies, setCookie] = useCookies(['brandsBrandType']);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	const fetchBrands = () => {
		setLoading(true);

		let order = '';
		if (sortOrder) {
			order = sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
		}
		httpService
			.get<BrandsSearchResponseModel>(
				`brands?page=${currentPage}&code=${brandCodeFilter}&description=${brandDescriptionFilter}&brandTypeId=${brandTypeFilter}&sortBy=${sortField}&order=${order}`,
				undefined
			)
			.then((brands) => {
				setBrandsSearch(brands);
				setBrandsListing(brands.models);
				setLoading(false);
			})
			.catch((err) => notifierService.error());
	};

	useEffect(() => {
		fetchBrands();
	}, [
		httpService,
		notifierService,
		currentPage,
		sortField,
		sortOrder,
		brandCodeFilter,
		brandDescriptionFilter,
		brandTypeFilter
	]);

	useEffect(() => {
		httpService
			.get<DropdownOption[]>('brandTypes', undefined)
			.then((options) => {
				setBrandTypesOptions(options);
				setBrandTypeFilter(
					cookies.brandsBrandType !== undefined
						? parseInt(cookies.brandsBrandType)
						: ''
				);
			})
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	useEffect(() => {
		if (shouldReload) {
			fetchBrands();
		}
	}, [shouldReload]);

	const handleInputTextChange = useCallback(
		(id: number, field: keyof BrandsListingResponseModel, value: any) => {
			const newBrands: BrandsListingResponseModel[] = [...brandsListing];
			const brandIndex: number = newBrands.findIndex((b) => b.id === id);

			if (brandIndex !== -1) {
				const targetItem = newBrands[brandIndex];
				(targetItem as any)[field] = value;

				if (field === 'brandTypeId') {
					const brandType = brandTypesOptions.find(
						(o) => o.value === value
					);
					if (!brandType) {
						return;
					}
					targetItem.brandTypeId = +brandType.value;
					targetItem.brandTypeCode = brandType.label;
				}

				setBrandsListing(newBrands);
			}
		},
		[brandsListing, setBrandsListing, brandTypesOptions]
	);

	const handleCellEditor = useCallback(
		(
			cellData: ColumnEditorOptions,
			field: keyof BrandsListingResponseModel
		) => {
			const cellValue = (cellData.rowData[field] as string) || '';
			return (
				<InputText
					value={cellValue}
					onChange={(e: ChangeEvent<HTMLInputElement>) => {
						//@ts-ignore
						cellData.editorCallback(e.target.value);
						handleInputTextChange(
							cellData.rowData.id,
							field,
							e.target.value
						);
					}}
				/>
			);
		},
		[handleInputTextChange]
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const handleInputSwitchChange = async (
		e: InputSwitchChangeParams,
		propName: keyof BrandsListingResponseModel
	) => {
		const brandId: number = +e.target.id;
		const brandIndex: number = brandsListing.findIndex(
			(b) => b.id === brandId
		);

		const brand = brandsListing[brandIndex];

		(brand as any)[propName] = e.target.value;

		try {
			await httpService.put(
				`brands/${brandId}/changeActive`,
				undefined,
				undefined,
				false
			);

			let order = '';
			if (sortOrder) {
				order =
					sortOrder === 1 ? 'asc' : sortOrder === -1 ? 'desc' : '';
			}

			const brands = await httpService.get<BrandsSearchResponseModel>(
				`brands?page=${currentPage}&code=${brandCodeFilter}&description=${brandDescriptionFilter}&brandTypeId=${brandTypeFilter}&sortBy=${sortField}&order=${order}`,
				undefined
			);

			setBrandsListing(brands.models);
		} catch {
			notifierService.error();
		}
	};

	const handleDropdownCellEditor = useCallback(
		(
			options: DropdownOption[],
			cellData: ColumnEditorOptions,
			field: keyof BrandsListingResponseModel
		) => {
			const cellValue = (cellData.rowData[field] as number) || '';
			return (
				<Dropdown
					value={cellValue}
					options={options}
					onChange={(e: DropdownChangeParams) => {
						cellData.rowData[field] = e.target.value;
						handleInputTextChange(
							cellData.rowData.id,
							field,
							e.target.value
						);
						handleEditorSubmit(cellData);
					}}
				/>
			);
		},
		[handleInputTextChange]
	);

	const onRenderActive = useCallback(
		(cellData: BrandsListingResponseModel) => {
			return (
				<InputSwitch
					id={cellData.id.toString()}
					checked={cellData.active}
					onChange={(e) => handleInputSwitchChange(e, 'active')}
				/>
			);
		},
		[handleInputSwitchChange]
	);

	const handleEditorSubmit = async (e: any) => {
		const { rowData } = e;
		try {
			await httpService.put(
				`brands/${rowData.id}`,
				JSON.stringify(rowData),
				undefined,
				false
			);
		} catch (err) {
			notifierService.error(err);
		}
	};

	const onPageChange = (event: PaginatorPageState) => {
		setFirst(event.first);
		setRows(event.rows);
		setCurrentPage(event.page! + 1);
	};

	const onSort = (event: DataTablePFSEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder);
	};

	return (
		<>
			<Filters
				brandTypesOptions={brandTypesOptions}
				setBrandCodeFilter={setBrandCodeFilter}
				setBrandDescriptionFilter={setBrandDescriptionFilter}
				brandTypeFilter={brandTypeFilter}
				setBrandTypeFilter={setBrandTypeFilter}
				setShouldReload={setShouldReload}
			/>

			<DataTable
				value={brandsListing}
				responsiveLayout='scroll'
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				loading={loading}
			>
				<Column
					style={{ width: '10%' }}
					sortable
					header='Status'
					body={onRenderActive}
					field={nameof<BrandsListingResponseModel>('active')}
				/>

				<Column
					style={{ width: '18%' }}
					sortable
					header='Brand code'
					field={nameof<BrandsListingResponseModel>('code')}
					editor={(ep) => handleCellEditor(ep, 'code')}
					onCellEditComplete={handleEditorSubmit}
				/>

				<Column
					style={{ width: '30%' }}
					sortable
					header='Brand description'
					field={nameof<BrandsListingResponseModel>('description')}
					editor={(ep) => handleCellEditor(ep, 'description')}
					onCellEditComplete={handleEditorSubmit}
				/>

				<Column
					sortable
					header='Brand Type'
					field={nameof<BrandsListingResponseModel>('brandTypeCode')}
					editor={(ep) =>
						handleDropdownCellEditor(
							brandTypesOptions,
							ep,
							'brandTypeId'
						)
					}
				/>
			</DataTable>

			<Pagination
				totalRecords={brandsSearch?.total}
				rows={rows}
				first={first}
				onPageChange={onPageChange}
			/>
		</>
	);
};

export default Brands;
